import {
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from 'react';

import { cn } from '@/shared/lib/utils';
import type { InputSize } from '@/shared/ui/input';
import { FieldLabel } from '@/shared/ui/form-field/FieldLabel';
import { InlineFieldLabel } from '@/shared/ui/form-field/InlineFieldLabel';
import { FieldMessage } from '@/shared/ui/form-field/FieldMessage';
import { ChevronDownIcon } from './icons';
import type { DateTimeChromeProps } from '../types';

const AFFIX_HEIGHT: Record<InputSize, string> = {
  xs: 'h-phx-control-xs',
  sm: 'h-phx-control-sm',
  md: 'h-phx-control',
  lg: 'h-phx-control-lg',
  xl: 'h-phx-control-xl',
};

const AFFIX_TEXT: Record<InputSize, string> = {
  xs: 'text-phx-xs',
  sm: 'text-phx-xs',
  md: 'text-phx-md',
  lg: 'text-phx-md',
  xl: 'text-phx-lg',
};

export interface FieldAffixProps extends DateTimeChromeProps {
  inputId: string;
  leadingIcon: ReactNode;
  placeholder: string;
  text: string;
  invalid: boolean;
  open: boolean;
  triggerAriaLabel: string;
  inputMode?: 'numeric';
  anchorRef: Ref<HTMLDivElement>;
  inputRef: Ref<HTMLInputElement>;
  onAffixMouseDown: (event: MouseEvent<HTMLDivElement>) => void;
  onInputChange: (value: string) => void;
  onInputFocus: () => void;
  onInputBlur: () => void;
  onInputKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onInputClick?: () => void;
  onToggle: (event: MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Field chrome shared by `DateBox` and `TimeBox`: label + affix group (leading icon, text input,
 * chevron trigger) + helper/error message. Mirrors the design's `.phx-affix` group with a controlled
 * text input; the popover itself is portalled by the parent box.
 */
export function FieldAffix({
  inputId,
  label,
  layout = 'stacked',
  labelWidth = 118,
  required,
  error,
  hint,
  disabled,
  size = 'md',
  fieldWidth,
  className,
  'data-testid': testId,
  leadingIcon,
  placeholder,
  text,
  invalid,
  open,
  triggerAriaLabel,
  inputMode,
  anchorRef,
  inputRef,
  onAffixMouseDown,
  onInputChange,
  onInputFocus,
  onInputBlur,
  onInputKeyDown,
  onInputClick,
  onToggle,
}: FieldAffixProps) {
  const messageId = `${inputId}-message`;
  const hasMessage = Boolean(error || hint);
  const isInline = layout === 'inline';

  const affix = (
    <div className="phx-field-control gap-phx-1_5 relative flex min-w-0 flex-1 flex-col">
      <div
        ref={anchorRef}
        data-required={required ? '' : undefined}
        data-invalid={invalid ? '' : undefined}
        onMouseDown={onAffixMouseDown}
        className={cn(
          'phx-affix rounded-phx pr-phx-1_5 flex min-w-0 cursor-text items-center border',
          'bg-phx-surface border-phx-border-2 transition-[border-color,box-shadow,background-color]',
          'hover:border-phx-border-3 focus-within:border-phx-primary focus-within:shadow-phx-focus',
          AFFIX_HEIGHT[size],
          required && 'bg-phx-required-tint focus-within:bg-phx-surface',
          invalid &&
            'border-phx-danger focus-within:border-phx-danger focus-within:shadow-[0_0_0_2px_rgba(181,52,42,0.25)]',
          disabled && 'bg-phx-surface-3 pointer-events-none cursor-not-allowed'
        )}
      >
        <span className="text-phx-text-muted pl-phx-3_5 flex flex-none items-center">
          {leadingIcon}
        </span>
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          inputMode={inputMode}
          value={text}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-describedby={hasMessage ? messageId : undefined}
          onChange={(event) => onInputChange(event.target.value)}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          onKeyDown={onInputKeyDown}
          onClick={onInputClick}
          className={cn(
            'text-phx-text placeholder:text-phx-text-faint px-phx-3_5 min-w-0 flex-1 border-0 bg-transparent outline-none',
            AFFIX_TEXT[size]
          )}
        />
        <button
          type="button"
          tabIndex={-1}
          data-open={open ? '' : undefined}
          onClick={onToggle}
          aria-label={triggerAriaLabel}
          className="text-phx-text-muted hover:bg-phx-surface-2 hover:text-phx-primary inline-flex h-6 w-6 flex-none cursor-pointer items-center justify-center rounded border-0 bg-transparent transition-colors"
        >
          <span className={cn('inline-flex transition-transform', open && 'rotate-180')}>
            <ChevronDownIcon />
          </span>
        </button>
      </div>
      {hasMessage ? (
        <FieldMessage id={messageId} variant={error ? 'error' : 'helper'}>
          {error ?? hint}
        </FieldMessage>
      ) : null}
    </div>
  );

  const rootStyle: CSSProperties = {
    width: fieldWidth ? `${fieldWidth}px` : '100%',
    maxWidth: '100%',
  };

  if (isInline) {
    return (
      <div
        data-layout="inline"
        data-required={required ? '' : undefined}
        className={cn('phx-field gap-phx-4 flex min-w-0 items-start', className)}
        style={rootStyle}
      >
        {label ? (
          <InlineFieldLabel
            htmlFor={inputId}
            required={required}
            disabled={disabled}
            width={labelWidth}
          >
            {label}
          </InlineFieldLabel>
        ) : null}
        {affix}
      </div>
    );
  }

  return (
    <div
      data-required={required ? '' : undefined}
      className={cn('phx-field gap-phx-2 flex min-w-0 flex-col', className)}
      style={rootStyle}
      data-testid={testId}
    >
      {label ? (
        <FieldLabel htmlFor={inputId} required={required} disabled={disabled}>
          {label}
        </FieldLabel>
      ) : null}
      {affix}
    </div>
  );
}
